import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import ViewColumnRoundedIcon from '@mui/icons-material/ViewColumnRounded';

import Nav from './nav';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';

const SLATE_TEXT = '#607086';

const COUNTRY_PREFIXES = [
  { code: '90', name: 'Turkey', flag: 'TR' },
  { code: '1', name: 'United States/Canada', flag: 'US' },
  { code: '44', name: 'United Kingdom', flag: 'GB' },
  { code: '33', name: 'France', flag: 'FR' },
  { code: '49', name: 'Germany', flag: 'DE' },
  { code: '34', name: 'Spain', flag: 'ES' },
  { code: '39', name: 'Italy', flag: 'IT' },
  { code: '91', name: 'India', flag: 'IN' },
  { code: '81', name: 'Japan', flag: 'JP' },
  { code: '86', name: 'China', flag: 'CN' }
];

const getCountryFromPhone = (phone) => {
  if (!phone) return { flag: 'ZZ', name: 'Unknown' };
  const digits = phone.replace(/[^\d]/g, '');
  const number = digits.startsWith('00') ? digits.slice(2) : digits;
  let match = null;

  for (const entry of COUNTRY_PREFIXES.sort((a, b) => b.code.length - a.code.length)) {
    if (number.startsWith(entry.code)) {
      match = entry;
      break;
    }
  }

  return match || { flag: 'ZZ', name: 'Unknown' };
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

const formatFieldLabel = (key) =>
  String(key || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getInitials = (value) => {
  if (!value) return '?';
  return String(value)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || '?';
};

const cellLabelSX = { fontSize: 12, color: SLATE_TEXT };

const MetricCard = ({ label, value, helper, Icon }) => (
  <Card
    sx={{
      height: '100%',
      borderRadius: 4,
      border: '1px solid',
      borderColor: alpha(PRIMARYCOLOR, 0.1),
      boxShadow: `0 12px 24px ${alpha(PRIMARYCOLOR, 0.05)}`
    }}
  >
    <CardContent sx={{ p: 2.1 }}>
      <Stack direction="row" spacing={1.4} alignItems="flex-start">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 3,
            bgcolor: alpha(PRIMARYCOLOR, 0.1),
            color: PRIMARYCOLOR,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Icon />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.45, fontWeight: 800, color: '#10233B', lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: SLATE_TEXT, lineHeight: 1.65 }}>
            {helper}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const renderValue = (value) => {
  if (!hasMeaningfulValue(value)) {
    return (
      <Typography variant="body2" sx={{ color: SLATE_TEXT }}>
        -
      </Typography>
    );
  }

  if (typeof value === 'boolean') {
    return (
      <Chip
        size="small"
        label={value ? 'True' : 'False'}
        sx={{
          fontWeight: 700,
          bgcolor: value ? alpha('#2E7D32', 0.1) : alpha('#D97706', 0.12),
          color: value ? '#2E7D32' : '#B45309'
        }}
      />
    );
  }

  let text = '';
  if (Array.isArray(value) || typeof value === 'object') {
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
  } else {
    text = String(value);
  }

  if (text.length > 72) {
    return (
      <Tooltip title={text}>
        <Box component="span" sx={{ display: 'inline-block', maxWidth: 260, color: '#20344B', lineHeight: 1.6 }}>
          {text.slice(0, 71)}...
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        px: 1,
        py: 0.55,
        borderRadius: 2,
        bgcolor: alpha(PRIMARYCOLOR, 0.06),
        color: '#20344B',
        border: '1px solid',
        borderColor: alpha(PRIMARYCOLOR, 0.08),
        lineHeight: 1.5
      }}
    >
      {text}
    </Box>
  );
};

const VishingFindings = () => {
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('name');
  const [selectedField, setSelectedField] = useState('all');

  const navigate = useNavigate();
  const { pentest_id } = useParams();

  const getUsers = useCallback(async (token) => {
    try {
      const response = await fetch(`${BASEURL}/pentests/vishing/users?pentest_id=${pentest_id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data?.error && data?.message === 'Invalid token') {
        navigate('/');
        return;
      }

      setUsersData(Array.isArray(data?.users) ? data.users : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsersData([]);
    }
  }, [navigate, pentest_id]);

  useEffect(() => {
    (async () => {
      const token = sessionStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      try {
        setLoading(true);
        await getUsers(token);
      } finally {
        setLoading(false);
      }
    })();
  }, [getUsers, navigate]);

  const usersWithFindings = useMemo(() => {
    return usersData
      .map((user) => {
        const rawResults = typeof user.results === 'string' ? user.results.trim() : '';
        if (!rawResults || rawResults === '{}') return null;

        try {
          const parsedResults = JSON.parse(rawResults);
          if (!parsedResults || typeof parsedResults !== 'object') return null;

          const cleanedResults = Object.fromEntries(
            Object.entries(parsedResults).filter(([, value]) => hasMeaningfulValue(value))
          );

          if (Object.keys(cleanedResults).length === 0) return null;
          return { ...user, parsedResults: cleanedResults };
        } catch {
          return {
            ...user,
            parsedResults: { captured_data: rawResults }
          };
        }
      })
      .filter(Boolean);
  }, [usersData]);

  const resultKeys = useMemo(() => {
    return Array.from(
      new Set(
        usersWithFindings.flatMap((user) => Object.keys(user.parsedResults || {}))
      )
    ).sort((left, right) => left.localeCompare(right));
  }, [usersWithFindings]);

  useEffect(() => {
    if (selectedField !== 'all' && !resultKeys.includes(selectedField)) {
      setSelectedField('all');
    }
  }, [resultKeys, selectedField]);

  const filteredUsers = useMemo(() => {
    return usersWithFindings.filter((user) => {
      if (selectedField !== 'all' && !hasMeaningfulValue(user.parsedResults?.[selectedField])) {
        return false;
      }

      if (!searchTerm) return true;
      const query = searchTerm.toLowerCase();

      if (searchCategory === 'name') return (user.name || '').toLowerCase().includes(query);
      if (searchCategory === 'phone') return (user.phone || '').toLowerCase().includes(query);
      if (searchCategory === 'results') {
        return JSON.stringify(user.parsedResults || {}).toLowerCase().includes(query);
      }

      return true;
    });
  }, [searchCategory, searchTerm, selectedField, usersWithFindings]);

  const countryCount = useMemo(() => {
    return new Set(filteredUsers.map((user) => getCountryFromPhone(user.phone).flag)).size;
  }, [filteredUsers]);

  const denseFindingCount = useMemo(() => {
    return filteredUsers.filter((user) => Object.keys(user.parsedResults || {}).length >= 3).length;
  }, [filteredUsers]);

  const metrics = [
    {
      label: 'Users With Findings',
      value: usersWithFindings.length,
      helper: 'Users where the vishing call captured non-empty data fields.',
      Icon: BadgeRoundedIcon
    },
    {
      label: 'Shown Right Now',
      value: filteredUsers.length,
      helper: 'Rows currently visible after field and search filtering.',
      Icon: TableRowsRoundedIcon
    },
    {
      label: 'Captured Fields',
      value: resultKeys.length,
      helper: 'Unique captured data fields detected across all finding rows.',
      Icon: ViewColumnRoundedIcon
    },
    {
      label: 'Countries',
      value: countryCount,
      helper: 'Country coverage represented by the filtered finding set.',
      Icon: PublicRoundedIcon
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F5F8FC' }}>
      <Nav />
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        <Card
          sx={{
            borderRadius: 4,
            border: '1px solid',
            borderColor: alpha(PRIMARYCOLOR, 0.1),
            boxShadow: `0 18px 32px ${alpha(PRIMARYCOLOR, 0.06)}`
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.6 } }}>
            <Stack spacing={2.3}>
              <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ xs: 'flex-start', lg: 'center' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
                    Captured Data Review
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 800, color: '#10233B' }}>
                    Findings
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.85, maxWidth: 760, color: SLATE_TEXT, lineHeight: 1.75 }}>
                    Review the data captured during successful vishing calls and quickly narrow the table by field or free-text search.
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  label={`${filteredUsers.length} of ${usersWithFindings.length || 0} shown`}
                  sx={{
                    fontWeight: 800,
                    bgcolor: alpha(PRIMARYCOLOR, 0.08),
                    color: PRIMARYCOLOR
                  }}
                />
              </Stack>

              <Grid container spacing={2}>
                {metrics.map((metric) => (
                  <Grid item xs={12} sm={6} lg={3} key={metric.label}>
                    <MetricCard {...metric} />
                  </Grid>
                ))}
              </Grid>

              <Box>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR, mb: 1 }}>
                  Field Filters
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <Chip
                    label="All Fields"
                    clickable
                    onClick={() => setSelectedField('all')}
                    sx={{
                      fontWeight: 700,
                      bgcolor: selectedField === 'all' ? PRIMARYCOLOR : '#ffffff',
                      color: selectedField === 'all' ? '#ffffff' : PRIMARYCOLOR,
                      border: '1px solid',
                      borderColor: selectedField === 'all' ? PRIMARYCOLOR : alpha(PRIMARYCOLOR, 0.16)
                    }}
                  />
                  {resultKeys.map((key) => (
                    <Chip
                      key={key}
                      label={formatFieldLabel(key)}
                      clickable
                      onClick={() => setSelectedField(key)}
                      sx={{
                        fontWeight: 700,
                        bgcolor: selectedField === key ? PRIMARYCOLOR : '#ffffff',
                        color: selectedField === key ? '#ffffff' : PRIMARYCOLOR,
                        border: '1px solid',
                        borderColor: selectedField === key ? PRIMARYCOLOR : alpha(PRIMARYCOLOR, 0.16)
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', md: 'center' }}>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 150 } }}>
                  <InputLabel>Filter By</InputLabel>
                  <Select value={searchCategory} label="Filter By" onChange={(event) => setSearchCategory(event.target.value)}>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="phone">Phone</MenuItem>
                    <MenuItem value="results">Results</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  placeholder="Search findings"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  sx={{ minWidth: { xs: '100%', md: 260 } }}
                />

                <Button variant="outlined" onClick={() => setSearchTerm('')}>
                  Clear
                </Button>

                <Chip
                  size="small"
                  label={`${denseFindingCount} rich finding${denseFindingCount === 1 ? '' : 's'}`}
                  sx={{ fontWeight: 700, bgcolor: '#ffffff', color: PRIMARYCOLOR, border: '1px solid', borderColor: alpha(PRIMARYCOLOR, 0.16) }}
                />
              </Stack>
            </Stack>
          </CardContent>

          <Divider />

          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: PRIMARYCOLOR,
                      color: '#ffffff',
                      fontWeight: 800,
                      letterSpacing: '0.03em',
                      borderBottom: 'none'
                    }
                  }}
                >
                  <TableCell>User</TableCell>
                  <TableCell>Phone</TableCell>
                  {resultKeys.map((key) => (
                    <TableCell key={key}>{formatFieldLabel(key)}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={2 + resultKeys.length} align="center" sx={{ py: 7 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    const country = getCountryFromPhone(user.phone);

                    return (
                      <TableRow
                        key={user.id}
                        hover
                        sx={{
                          '& td': { borderBottomColor: alpha(PRIMARYCOLOR, 0.06), verticalAlign: 'top' },
                          '&:nth-of-type(odd)': { backgroundColor: alpha(PRIMARYCOLOR, 0.016) },
                          '&:hover': { backgroundColor: alpha(PRIMARYCOLOR, 0.05) }
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(PRIMARYCOLOR, 0.12), color: PRIMARYCOLOR }}>
                              {getInitials(user.name)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: '#10233B' }}>{user.name || 'Unknown user'}</Typography>
                              <Typography sx={cellLabelSX}>ID: {user.id}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                fontSize: '0.78rem',
                                bgcolor: alpha(PRIMARYCOLOR, 0.08),
                                color: PRIMARYCOLOR
                              }}
                            >
                              {country.flag}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600, color: '#20344B' }}>{user.phone || '-'}</Typography>
                              <Typography sx={cellLabelSX}>{country.name}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {resultKeys.map((key) => (
                          <TableCell key={key}>
                            <Typography variant="body2" sx={{ color: '#20344B', lineHeight: 1.6 }}>
                              {renderValue(user.parsedResults[key])}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={2 + resultKeys.length} align="center" sx={{ py: 7 }}>
                      <Typography sx={{ fontWeight: 700, color: '#10233B' }}>No findings available.</Typography>
                      <Typography variant="body2" sx={{ mt: 0.6, color: SLATE_TEXT }}>
                        Try a different field filter or search term.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </Box>
  );
};

export default VishingFindings;
